import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { CheckCircle, Loader2, Coins } from "lucide-react";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get("payment_id");
  const paymentType = searchParams.get("type");
  const npId =
    searchParams.get("NP_id") ||
    searchParams.get("np_id") ||
    searchParams.get("nowpay_payment_id") ||
    searchParams.get("payment_id_np"); // NOWPayments payment_id (redirect param name may vary)
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [credits, setCredits] = useState(0);
  const [productType, setProductType] = useState("credit_purchase");
  const [subscriptionDays, setSubscriptionDays] = useState(null);
  const attemptsRef = useRef(0);

  const pollPaymentStatus = useCallback(async () => {
    const maxAttempts = 200; // 10 minutes total
    const pollInterval = 3000;

    if (attemptsRef.current >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    attemptsRef.current += 1;

    try {
      const token = localStorage.getItem("token");
      if (token) {
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }
      
      // Only crypto payments now
      const endpoint = `/credits/crypto-status/${paymentId}`;
      const response = await api.get(endpoint, {
        params: npId ? { np_id: npId } : undefined,
      });
      
      const paymentStatus = response.data?.status;
      const debugInfo = response.data?.debug_info;
      
      console.log(`[Payment Check ${attemptsRef.current}] Status: ${paymentStatus}`, response.data);
      
      // Check for paid status (handle all variations - be very permissive)
      const isPaid = paymentStatus === "paid" || 
                     paymentStatus === "finished" || 
                     paymentStatus === "confirmed" ||
                     (debugInfo && (debugInfo.nowpay_status === "finished" || debugInfo.nowpay_status === "confirmed")) ||
                     (debugInfo && (debugInfo.db_status === "paid" || debugInfo.db_status === "finished"));
      
      if (isPaid) {
        console.log(`[Payment Check ${attemptsRef.current}] Payment is PAID! Stopping polling.`);
        setStatus("success");
        setCredits(
          response.data?.credits ??
            response.data?.amount ??
            0
        );
        setProductType(response.data?.product_type || "credit_purchase");
        setSubscriptionDays(response.data?.days ?? null);
        await refreshUser();
        return;
      }
      
      // Check for failed/expired statuses
      if (paymentStatus === "expired" || paymentStatus === "failed" || paymentStatus === "refunded") {
        console.log(`[Payment Check ${attemptsRef.current}] Payment ${paymentStatus}. Stopping polling.`);
        setStatus("expired");
        return;
      }

      // Continue polling for waiting/confirming/sending statuses
      console.log(`[Payment Check ${attemptsRef.current}] Payment still ${paymentStatus}. Continuing polling...`);
      setTimeout(() => pollPaymentStatus(), pollInterval);
    } catch (error) {
      console.error(`[Payment Check ${attemptsRef.current}] Error:`, error);
      // Don't give up on errors - continue polling (might be temporary network issue)
      setTimeout(() => pollPaymentStatus(), pollInterval);
    }
  }, [npId, paymentId, refreshUser]);

  useEffect(() => {
    if (paymentId) {
      attemptsRef.current = 0;
      pollPaymentStatus();
    } else {
      // URL'de payment_id yok (sayfa doğrudan açıldı veya yanlış yönlendirme)
      setStatus("no_payment_id");
    }
  }, [paymentId, pollPaymentStatus]);

  return (
    <div className="min-h-screen grid-texture flex items-center justify-center p-4">
      <div className="glass p-8 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-mono font-bold mb-2">ÖDEME KONTROL EDİLİYOR</h1>
            <p className="text-muted-foreground mb-6">Lütfen bekleyin...</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-mono font-bold mb-2 text-primary">ÖDEME BAŞARILI!</h1>
            {productType === "subscription_purchase" && subscriptionDays ? (
              <div className="flex items-center justify-center gap-2 mb-4">
                <Coins className="w-6 h-6 text-primary" />
                <span className="text-2xl font-mono font-bold">{subscriptionDays}</span>
                <span className="text-muted-foreground">gün abonelik aktif</span>
              </div>
            ) : (productType === "api_balance_topup" || credits > 0) && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <Coins className="w-6 h-6 text-primary" />
                <span className="text-2xl font-mono font-bold">${Number(credits || 0).toFixed(0)}</span>
                <span className="text-muted-foreground">API bakiyesi eklendi</span>
              </div>
            )}
            <p className="text-muted-foreground mb-6">
              {productType === "subscription_purchase"
                ? "Aboneliğiniz hesabınıza tanımlandı. Süre boyunca panel sepetinden sipariş verebilirsiniz."
                : productType === "api_balance_topup"
                  ? "API bakiyeniz hesabınıza eklendi. Müşteri API ile basım yapabilirsiniz."
                  : "Bakiyeniz hesabınıza eklendi."}
            </p>
            <Link to="/dashboard">
              <Button className="font-mono uppercase bg-primary text-black hover:bg-primary/90 rounded-none" data-testid="go-to-dashboard">
                DASHBOARD'A GİT
              </Button>
            </Link>
          </>
        )}

        {status === "timeout" && (
          <>
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⏳</span>
            </div>
            <h1 className="text-2xl font-mono font-bold mb-2">ÖDEME BEKLENİYOR</h1>
            <p className="text-muted-foreground mb-6">
              Ödeme durumu henüz onaylanmadı. Lütfen birkaç dakika bekleyin veya dashboard'dan kontrol edin.
            </p>
            <Link to="/dashboard">
              <Button className="font-mono uppercase bg-primary text-black hover:bg-primary/90 rounded-none">
                DASHBOARD'A GİT
              </Button>
            </Link>
          </>
        )}

        {status === "expired" && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">✕</span>
            </div>
            <h1 className="text-2xl font-mono font-bold mb-2 text-red-400">ÖDEME SÜRESİ DOLDU</h1>
            <p className="text-muted-foreground mb-6">
              Ödeme süresi doldu. Lütfen tekrar deneyin.
            </p>
            <Link to="/credits">
              <Button className="font-mono uppercase bg-primary text-black hover:bg-primary/90 rounded-none">
                ABONELİK PAKETLERİNE DÖN
              </Button>
            </Link>
          </>
        )}

        {status === "no_payment_id" && (
          <>
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">?</span>
            </div>
            <h1 className="text-2xl font-mono font-bold mb-2">ÖDEME BİLGİSİ BULUNAMADI</h1>
            <p className="text-muted-foreground mb-6">
              Bu sayfaya doğrudan gelindi veya yönlendirme bilgisi eksik. Ödemeniz tamamsa dashboard üzerinden abonelik durumunuzu kontrol edebilirsiniz.
            </p>
            <Link to="/dashboard">
              <Button className="font-mono uppercase bg-primary text-black hover:bg-primary/90 rounded-none">
                DASHBOARD'A GİT
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
